import React, { useState, useEffect, useMemo } from "react";
import "./Signup.css";
import { useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight, Loader2, Info } from "lucide-react";

const TOTAL_STEPS = 4;

const Signup = () => {
  const [businessName, setBusinessName] = useState("");
  const [businessUrl, setBusinessUrl] = useState("");
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState(""); // 'error', 'success', 'info'
  const [showVerification, setShowVerification] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [businessNameError, setBusinessNameError] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [originalScrollPosition, setOriginalScrollPosition] = useState(0);

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const isPopup = new URLSearchParams(window.location.search).get("popup") === "true";

  // Step logic: Step 1 = signup form, Step 2 = verification
  const currentStep = showVerification ? 2 : 1;

  const progressPct = useMemo(() => {
    const pct = Math.round((currentStep / TOTAL_STEPS) * 100);
    return Math.max(0, Math.min(100, pct));
  }, [currentStep]);

  // --- Browser/Device Detection ---
  const ua = navigator.userAgent;
  const isIOS = /iP(ad|od|hone)/i.test(ua);
  const isSafari = /^((?!chrome|crios|fxios).)*safari/i.test(ua);

  if (isIOS && isSafari) {
    document.body.classList.add("safari-mobile");
  }

  const detectMobile = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) ||
      (window.innerWidth <= 768 && "ontouchstart" in window)
    );
  };

  useEffect(() => {
    setIsMobile(detectMobile());

    // Force onboarding page background + remove any gradient from parent layouts
    document.body.classList.add("nx-onboard-body");
    return () => {
      document.body.classList.remove("nx-onboard-body");
    };
  }, []);

  // --- TrustLock Badge Logic (unchanged) ---
  useEffect(() => {
    let scriptLoaded = false;

    const initializeTrustLockBadge = () => {
      const badgeElement = document.getElementById("trustlock-badge");
      if (!badgeElement) return false;

      badgeElement.style.display = "block";

      try {
        document.dispatchEvent(new CustomEvent("trustlock:scan"));
      } catch (e) {}

      if (!scriptLoaded) {
        const script = document.createElement("script");
        script.src = "https://app.trustlock.co/js/seal-api.js";
        script.async = true;
        script.onload = () => {
          scriptLoaded = true;
          setTimeout(initializeTrustLockBadge, 500);
        };
        document.head.appendChild(script);
        return true;
      }
      return false;
    };

    setTimeout(initializeTrustLockBadge, 100);
  }, []);

  // --- Input Logic ---
  const handleInputFocus = () => {
    if (isMobile) setOriginalScrollPosition(window.pageYOffset || document.documentElement.scrollTop);
  };

  const handleInputBlur = () => {
    if (isMobile && originalScrollPosition !== undefined) {
      setTimeout(() => window.scrollTo({ top: originalScrollPosition, behavior: "smooth" }), 100);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    if (value && index < 5) document.getElementById(`code-${index + 1}`)?.focus();
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      document.getElementById(`code-${index - 1}`)?.focus();
    }
  };

  // --- Submit Logic ---
  const updateStatus = (type, message) => {
    setStatusType(type);
    setStatusMessage(message);
  };

  const validateEmail = (emailStr) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);

  const handleInitialSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      updateStatus("error", "Please enter a valid email address");
      return;
    }
    if (businessName.trim() === "") {
      updateStatus("error", "Please enter your business name");
      return;
    }

    setLoading(true);
    updateStatus("info", "Checking website accessibility...");

    try {
      const accessResponse = await fetch(`${apiUrl}/api/signup/check-website`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: businessUrl.trim() }),
      });

      if (!accessResponse.ok) {
        const data = await accessResponse.json().catch(() => ({}));
        updateStatus("error", data.detail || "Website is not accessible");
        setLoading(false);
        return;
      }

      updateStatus("info", "Sending verification code...");
      const response = await fetch(`${apiUrl}/api/signup/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_url: businessUrl, email, business_name: businessName }),
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        if (data.requires_verification) {
          setShowVerification(true);
          updateStatus("success", "Code sent! Check your email.");
        } else {
          updateStatus("success", data.message || "Success!");
        }
      } else {
        updateStatus("error", data.detail || "An error occurred.");
      }
    } catch (error) {
      updateStatus("error", "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    const fullCode = verificationCode.join("");
    if (fullCode.length !== 6) {
      updateStatus("error", "Enter the full 6-digit code");
      return;
    }

    setLoading(true);
    updateStatus("info", "Verifying...");

    try {
      const response = await fetch(`${apiUrl}/api/signup/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: fullCode }),
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        updateStatus("success", "Verified! Redirecting...");
        if (isPopup && window.opener) {
          window.opener.postMessage({ type: "SIGNUP_COMPLETE" }, window.location.origin);
        } else {
          setTimeout(() => navigate(data.redirect, { replace: true }), 1200);
        }
      } else {
        updateStatus("error", data.detail || "Invalid code.");
      }
    } catch (error) {
      updateStatus("error", "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendDisabled) return;
    setLoading(true);
    updateStatus("info", "Resending...");
    setResendDisabled(true);

    try {
      const response = await fetch(`${apiUrl}/api/signup/resend-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        updateStatus("success", "Code resent!");
        setTimeout(() => setResendDisabled(false), 60000);
      } else {
        updateStatus("error", "Failed to resend.");
        setResendDisabled(false);
      }
    } catch {
      updateStatus("error", "Error resending code.");
      setResendDisabled(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`nx-onboard-page nx-signup-page ${isPopup ? "nx-is-popup" : ""}`}>
      {/* Top bar */}
    <div className="nx-topbar">
      <div className="nx-brand">
        <div className="nx-brand-mark">
          <img className="nx-brand-logo" src="/images/NexOpta_Icon.png" alt="Nexopta" />
        </div>
        <div className="nx-brand-word">NEXOPTA</div>
      </div>

      <div className="nx-step">
        Step {currentStep} of {TOTAL_STEPS}
      </div>
    </div>


      {/* Progress bar */}
      <div className="nx-progress-wrap">
        <div className="nx-progress-track">
          <div className="nx-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Content */}
      <div className="nx-content">
        <div className="nx-grid nx-grid-signup">
          {/* LEFT: Form Card */}
          <div className="nx-form-card">
            <div className="nx-form-header">
              <h2>{showVerification ? "Verify your email" : "Let's get started"}</h2>
              <p className="nx-subtitle">
                {showVerification
                  ? <>Enter the code sent to <strong>{email}</strong></>
                  : "Enter your business details to begin the setup."}
              </p>
            </div>

            {/* Status Banner */}
            {statusMessage && (
              <div className={`nx-status-banner ${statusType}`}>
                {loading ? (
                  <Loader2 className="nx-spinner" size={18} />
                ) : statusType === "success" ? (
                  <Check size={18} />
                ) : (
                  <Info size={18} />
                )}
                <span>{statusMessage}</span>
              </div>
            )}

            {!showVerification ? (
              <>
                <form onSubmit={handleInitialSubmit} className="nx-form">
                  <div className="nx-field">
                    <label>Business Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Acme Corp"
                      value={businessName}
                      onChange={(e) => {
                        setBusinessName(e.target.value);
                        setBusinessNameError(e.target.value.length > 40 ? "Name too long" : "");
                      }}
                      className={businessNameError ? "nx-input-error" : ""}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      required
                    />
                    {businessNameError && <span className="nx-field-error">{businessNameError}</span>}
                  </div>

                  <div className="nx-field">
                    <label>Business Email</label>
                    <input
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      required
                    />
                  </div>

                  <div className="nx-field">
                    <label>Company Website</label>
                    <input
                      type="text"
                      placeholder="https://www.yourcompany.com"
                      value={businessUrl}
                      onChange={(e) => setBusinessUrl(e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="nx-primary-btn"
                    disabled={loading || !!businessNameError}
                  >
                    {loading ? "Processing..." : "Continue"}
                    {!loading && <ArrowRight size={18} />}
                  </button>
                </form>

                <p className="nx-login-link">
                  Already have an account? <Link to="/login">Log In</Link>
                </p>
              </>
            ) : (
              <>
                <form onSubmit={handleVerificationSubmit} className="nx-verify-form">
                  <div className="nx-code-grid">
                    {verificationCode.map((digit, idx) => (
                      <input
                        key={idx}
                        id={`code-${idx}`}
                        type="tel"
                        maxLength={1}
                        className="nx-code-box"
                        value={digit}
                        onChange={(e) => handleCodeChange(idx, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                        inputMode="numeric"
                        disabled={loading}
                      />
                    ))}
                  </div>

                  <button type="submit" className="nx-primary-btn" disabled={loading}>
                    {loading ? "Verifying..." : "Verify"}
                  </button>

                  <div className="nx-resend">
                    <span className="nx-resend-label">Didn't receive the email?</span>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      className="nx-resend-link"
                      disabled={resendDisabled || loading}
                    >
                      {resendDisabled ? "Wait to resend" : "Resend Code"}
                    </button>
                  </div>
                </form>

              </>
            )}
          </div>

          {/* RIGHT: Info Panel */}
          <div className="nx-side-col">
            <div className="nx-info-card">
              <div className="nx-info-header">
                <div className="nx-info-badge">
                  <Info size={16} />
                </div>
                <h3>Why we need this</h3>
              </div>

              <p>
                Your business email serves as your unique identifier and login credential.
                We use your website URL to pre-configure your assistant with industry-specific
                knowledge before you even start.
              </p>

              <ul className="nx-info-list">
                <li>
                  <Check size={16} />
                  <span>
                    <strong>Secure Login:</strong> Your email creates your secure account.
                  </span>
                </li>
              </ul>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
